import React, {
  Fragment,
  useState,
  useMemo,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { ApolloConsumer } from "@apollo/client";
import { Link } from "react-router-dom";
import { MdClose } from "react-icons/md";
import ReactTooltip from "react-tooltip";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { RenderDate } from "../CaseDetails/CaseDetailUtils";
import NoDataComponent from "./../../../components/Table/TableComponents/NoDataComponent";
import { CasesContext } from "../CasesContext";
import { FaRegBell } from "react-icons/fa";
import "./../../../components/Cases/cases.scss";

import {
  GET_SUBMITTED_PAGINATED_DATA,
  GET_HISTORICAL_PAGINATED_DATA,
  GET_INITIATED_PAGINATED_DATA,
  GET_PRESUBMITTED_PAGINATED_DATA,
  GET_APPEAL_PAGINATED_DATA,
  GET_REGISTRATION_NUMBER_PAGINATED_DATA,
} from "../../../screens/Cases/queries";

import {
  // Styles,
  // PageInput,
  // PageNavButton,
  // Pagination,
  // TD,
  // TH_Container,
  // TH,
  SubComponent,
  CaseCounter,
  StatusPill,
  TDIcon,
  RefineSearch,
} from "../../../global/css/component/Table";

import CaseTableActions from "./CaseTableActions";
import { FaPencilAlt, FaFilePdf, FaSpinner, FaFilter } from "react-icons/fa";
import getpdf from "../../Utils/PDFLink";
// import styling for refine search filter and modal window
import "../CasesFilter/CaseFilter.scss";
import Modal from "react-modal";
import CasesFilter from "../CasesFilter";
import Dot from "../NotificationDot";
// import { TableLoadingUI } from "../../SimpleForm/ReactTable/ReactTableStyles";
import { ReactTable } from "../../SimpleForm/ReactTable";
import { useDebouncedFunction } from "../../Hooks";

const EMPTY_CASE_ROWS = [];

const renderRowSubComponent = (row, table_type, caseType) => {
  const {
    application_id,
    case_number,
    visaType,
    caseNumber,
    empBusinessName,
    dateSubmitted,
    jobTitle,
    externalStatus,
    caseStatus,
    caseMetadata,
    applicationId,
  } = row.original;
  let _caseNumber = ["Submitted", "Historical"].includes(table_type)
    ? caseNumber
    : case_number;
  return (
    <SubComponent>
      <div className="column-align grid-row">
        <div className="right-align">
          {table_type === "Submitted" && caseType === "9142B" && (
            <p>Randomization Information </p>
          )}
        </div>

        <div className="left-align">
          {table_type === "Submitted" && caseType === "9142B" && (
            <p>{caseMetadata.randomizationInfo || "N/A"}</p>
          )}
          {table_type === "Submitted" ||
          table_type === "Presubmitted" ||
          table_type === "Historical" ? (
            <Link
              to={
                table_type !== "Presubmitted"
                  ? {
                      pathname: `/dashboard/case/${caseType}/${_caseNumber}`,
                      state: {
                        externalStatus,
                        caseStatus,
                        empBusinessName,
                        jobTitle,
                        dateSubmitted,
                        visaType,
                        app_id: applicationId,
                        caseMetadata,
                      },
                    }
                  : ""
              }
              rel="noopener noreferrer"
              style={
                table_type === "Presubmitted"
                  ? { "pointer-events": "none" }
                  : undefined
              }
              disabled={table_type === "Presubmitted"}
            >
              <button
                disabled={table_type === "Presubmitted"}
                className="usa-button expand-edit-btn"
              >
                Case Detail
              </button>
            </Link>
          ) : (
            <Link
              to={`/dashboard/application/${caseType}/${application_id}`}
              rel="noopener noreferrer"
            >
              <button className="usa-button expand-edit-btn">
                {" "}
                <FaPencilAlt /> Edit
              </button>
            </Link>
          )}
        </div>
      </div>
    </SubComponent>
  );
};

const sortColumnResolver = (id) => {
  const resolve = {
    submitted_date: "submitted_date",
    employer: "employer",
    soc_title: "soc_title",
    foreign_worker_name: "foreign_worker_name",
    external_status: "external_status",
    application_id: "application_id",
    created: "created",
    submitted: "submitted",
    empBusinessName: "empBusinessName",
    jobTitle: "jobTitle",
    hasActiveNotification: "hasActiveNotification",
    dateSubmitted: "dateSubmitted",
    createdDate: "createdDate",
    registrationNumber: "registrationNumber",
    externalStatus: "externalStatus",
    caseNumber: "caseNumber",
    notification: "notification",
    appealsReceiptDate: "appealsReceiptDate",
  };
  return resolve[id] ? resolve[id] : id;
};

function CasesTable({
  navCounts,
  apolloClient,
  groupRole,
  groupMembers,
  refetchTabsTable,
}) {
  const queryResolver = (type) => {
    const resolver = {
      Initiated: GET_INITIATED_PAGINATED_DATA,
      Presubmitted: GET_PRESUBMITTED_PAGINATED_DATA,
      Submitted: GET_SUBMITTED_PAGINATED_DATA,
      Historical: GET_HISTORICAL_PAGINATED_DATA,
      Appeal: GET_APPEAL_PAGINATED_DATA,
      RegistrationNumber: GET_REGISTRATION_NUMBER_PAGINATED_DATA,
    };
    return resolver[type];
  };
  const [selectedAll, setSelectedAll] = useState(false);
  const { activeTab, activeNav, filterBy, setCheckedCases, checkedCases } =
    useContext(CasesContext);
  const tabCounts = navCounts[`Form${activeNav.machine_name}`];
  const count = tabCounts[activeTab.machine_name]?.Total;
  let caseType = activeNav.machine_name;
  let table_type = activeTab.machine_name;
  let query = queryResolver(activeTab.machine_name);
  const is_initiated = table_type === "Initiated";
  const is_presubmitted = table_type === "Presubmitted";
  const is_historical = table_type === "Historical";
  const is_appeal = table_type === "Appeal";
  const isAssignedCaseNumber = table_type === "Submitted";
  const is_initiated_or_presubmitted =
    is_initiated || table_type === "Presubmitted";
  const is_9089 = caseType === "9089";

  useEffect(() => {
    // Cannot avoid double refetch on very first render. Maybe utilize useLazyQuery/skip options in future to prevent this
    refetchTabsTable();
  }, []);

  // We ought to have ReactTooltip be a global component, at which point we can dynamically anchor it
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [activeTab.machine_name, activeNav.machine_name]);

  const handleSelectAll = (e, all) => {
    const isChecked = e?.currentTarget?.checked;
    const rows = all?.data || [];
    setSelectedAll(isChecked);
    if (isChecked) {
      const newCheckedCases = rows.map(({ application_id, applicationId }) => {
        application_id = application_id ? application_id : applicationId;

        return {
          application_id,
        };
      });
      return setCheckedCases([...newCheckedCases]);
    }
    setCheckedCases([]);
  };
  const handleSelect = (e, caseSelected) => {
    const isChecked = e?.currentTarget?.checked;
    if (isChecked) {
      return setCheckedCases([...checkedCases, caseSelected]);
    }
    const newCheckedCases = checkedCases.filter(
      (n) => n.application_id !== caseSelected.application_id
    );
    setCheckedCases(newCheckedCases);
  };

  let showCheckbox;
  if (
    (!groupRole && table_type === "Initiated") ||
    (groupRole === "Subaccount" && table_type === "Initiated") ||
    (groupRole === "Master" && table_type !== "Presubmitted")
  ) {
    showCheckbox = true;
  }

  let memoizedColumns = [];

  if (table_type === "RegistrationNumber") {
    memoizedColumns = [
      {
        Header: "Registration Number",
        accessor: "registrationNumber",
        id: "registrationNumber",
        fixedWidth: 200,
        Cell: ({ cell: { value, row } }) => (
          <>
            {row.original.registrationNumber && (
              <Link
                to={{
                  pathname: `/dashboard/registration/${caseType}/${value}`,
                  state: {
                    prevPageTitle: "Registration Numbers",
                    prevPageUrl: window.location.pathname,
                  },
                }}
                rel="noopener noreferrer"
              >
                <u>
                  <a className="usa-link">{value || "--"}</a>
                </u>
              </Link>
            )}
          </>
        ),
      },
      {
        Header: "Employer Name",
        accessor: "empBusinessName",
        id: "empBusinessName",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`empBusinessName_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`empBusinessName_${index}`}
            >
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Job Title",
        accessor: "jobTitle",
        id: "jobTitle",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`jobTitle_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip type="dark" effect="solid" id={`jobTitle_${index}`}>
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Primary Worksite MSA",
        accessor: "jobMsa",
        id: "jobMsa",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`jobMsa_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip type="dark" effect="solid" id={`jobMsa_${index}`}>
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Workers",
        accessor: "numberOfWorkers",
        id: "numberOfWorkers",
        fixedWidth: 100,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`workers_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip type="dark" effect="solid" id={`workers_${index}`}>
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Valid From",
        accessor: "validityStartDate",
        id: "validityStartDate",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => {
          return RenderDate(value, index, "", "UTC");
        },
      },
      {
        Header: "Valid To",
        accessor: "validityEndDate",
        id: "validityEndDate",
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => {
          value = Number(value);
          const _date = value
            ? momentTimezone(value).tz("UTC").format("MMMM D, YYYY")
            : null;
          return (
            <>
              <span data-tip data-for={`validityEndDate_${index}`}>
                {_date || "--"}
              </span>
              <ReactTooltip
                type="dark"
                effect="solid"
                id={`validityEndDate_${index}`}
              >
                <span>{_date || "--"}</span>
              </ReactTooltip>
            </>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        id: "status",
        Cell: ({ cell: { value } }) => {
          const _value = value ? value : "";
          return (
            <>
              <StatusPill status={_value.replace(/\s+/g, "-").toLowerCase()}>
                <span>{_value}</span>
              </StatusPill>
            </>
          );
        },
      },
    ];
  } else {
    memoizedColumns = [
      {
        Header: (all) => {
          if (showCheckbox) {
            return (
              <>
                <input
                  type="checkbox"
                  name="pwd-selection"
                  className="usa-checkbox__input"
                  id={`allListedCases`}
                  title="Select All"
                  onChange={(e) => e}
                  checked={selectedAll}
                  onClick={(e) => handleSelectAll(e, all)}
                  value={""}
                />
                <label
                  className="usa-checkbox__label"
                  style={{ padding: 0, margin: "0 -3px" }}
                  htmlFor={`allListedCases`}
                  onChange={(e) => e}
                  checked={selectedAll}
                />
              </>
            );
          }
          return null;
        },
        id: "checkbox", // It needs an ID
        fixedWidth: 40,
        show: showCheckbox,
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          let { application_id, applicationId } = original;
          application_id = application_id ? application_id : applicationId;
          let checked =
            checkedCases &&
            checkedCases.some((n) => {
              return n.application_id === application_id;
            });
          return (
            <>
              <input
                type="checkbox"
                name="pwd-selection"
                title={application_id}
                className="usa-checkbox__input"
                id={`${application_id}`}
                onChange={(e) => e}
                checked={checked}
                onClick={(e) => handleSelect(e, { application_id })}
                value={application_id}
              />
              <label
                className="usa-checkbox__label"
                htmlFor={`${application_id}`}
                onChange={(e) => e}
                style={{
                  padding: 0,
                  margin: "0 0 0 8px",
                }}
                checked={checked}
              />
            </>
          );
        },
      },
      {
        Header: <FaRegBell />,
        accessor: "hasActiveNotification",
        id: "hasActiveNotification",
        fixedWidth: 34,
        show: !is_initiated_or_presubmitted,
        Cell: ({ row }) => {
          if (row.original.hasActiveNotification) {
            return <Dot text={row.original.caseNumber} />;
          }
          return null;
        },
      },
      {
        Header: "Assigned Case Number",
        accessor: "caseNumber",
        id: "caseNumber",
        fixedWidth: caseType === "790A" ? 210 : 200,
        show: !is_initiated_or_presubmitted,
        Cell: ({ cell: { value, row } }) => (
          <>
            {row.original.applicationId && (
              <Link
                to={{
                  pathname: `/dashboard/case/${caseType}/${value}`,
                  state: {
                    prevPageTitle: "Cases",
                    prevPageUrl: window.location.pathname,
                    isHistorical: is_historical,
                    isAppeal: is_appeal,
                    isAssignedCaseNumber,
                  },
                }}
                rel="noopener noreferrer"
              >
                <u>
                  <a className="usa-link">{value || "--"}</a>
                </u>
              </Link>
            )}
          </>
        ),
      },
      {
        Header: !is_initiated ? "Pending Case Number" : "Application ID",
        accessor: "application_id",
        id: "application_id",
        fixedWidth: 245,
        show: is_initiated_or_presubmitted,
        Cell: ({
          cell: {
            value,
            row: { index, original },
          },
        }) => {
          if (original) {
            if (is_initiated) {
              const { formVersionId } = original;
              let formVersionIdPath = "";
              if (
                !!formVersionId &&
                (caseType === "790A" || caseType === "9142A")
              ) {
                formVersionIdPath = `/V${formVersionId}`;
              }
              return (
                <Link
                  to={{
                    pathname: `/dashboard/application/${caseType}/${original?.application_id}${formVersionIdPath}`,
                    state: {
                      prevPageTitle: "Cases",
                      prevPageUrl: window.location.pathname,
                    },
                  }}
                  data-tip
                  data-for={`application_id${index}`}
                  rel="noopener noreferrer"
                >
                  <u>
                    <a className="usa-link">{value || "--"}</a>
                  </u>
                </Link>
              );
            } else if (is_presubmitted) {
              return (
                <span
                  data-tip
                  data-for={`application_id${index}`}
                  rel="noopener noreferrer"
                >
                  <span>{value || "--"}</span>
                </span>
              );
            }
            return null;
          } else {
            return "--";
          }
        },
      },
      {
        Header: "Employer Name",
        accessor: "employer",
        id: "employer",
        show: is_initiated_or_presubmitted,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`employer_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip type="dark" effect="solid" id={`employer_${index}`}>
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Employer Name",
        accessor: "empBusinessName",
        id: "empBusinessName",
        show: !is_initiated_or_presubmitted,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`empBusinessName_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`empBusinessName_${index}`}
            >
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Job Title",
        accessor: "soc_title",
        id: "soc_title",
        show: is_initiated_or_presubmitted && !is_9089,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`soc_title_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip type="dark" effect="solid" id={`soc_title_${index}`}>
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Job Title",
        accessor: "jobTitle",
        id: "jobTitle",
        show: !is_initiated_or_presubmitted || is_9089,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`jobTitle_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip type="dark" effect="solid" id={`jobTitle_${index}`}>
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Foreign Worker Name",
        accessor: "foreign_worker_name",
        id: "foreign_worker_name",
        show: is_9089,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => (
          <>
            <span data-tip data-for={`foreign_worker_name_${index}`}>
              {value || "--"}
            </span>
            <ReactTooltip
              type="dark"
              effect="solid"
              id={`foreign_worker_name_${index}`}
            >
              <span>{value || "--"}</span>
            </ReactTooltip>
          </>
        ),
      },
      {
        Header: "Submitted Date",
        accessor: "dateSubmitted",
        id: "dateSubmitted",
        fixedWidth: 165,
        show: !is_initiated_or_presubmitted,
        Cell: ({ cell: { value }, row: { id } }) => {
          const _date = momentTimezone(Number(value))
            .tz("America/New_York")
            .format("MMMM D, YYYY");

          const tooltipId = `submitted-date-tooltip-${value}-${id}`;

          return (
            <Fragment>
              <span
                className="cell changed ellipsis"
                data-tip
                data-for={tooltipId}
              >
                {_date}
              </span>
              <ReactTooltip id={tooltipId} type="dark" effect="solid">
                <span>{_date}</span>
              </ReactTooltip>
            </Fragment>
          );
        },
      },
      {
        Header: "Determination Date",
        accessor: "caseMetadata.determinationDate",
        id: "caseMetadata.determinationDate",
        fixedWidth: 165,
        show: is_9089 && is_historical,
        Cell: ({ cell: { value }, row: { id } }) => {
          value = Number(value);
          const _date = value
            ? momentTimezone(value)
                .tz("America/New_York")
                .format("MMMM D, YYYY")
            : null;

          const tooltipId = `determination-date-tooltip-${value}-${id}`;
          return (
            <>
              <span data-tip data-for={tooltipId}>
                {_date || "--"}
              </span>
              <ReactTooltip type="dark" effect="solid" id={tooltipId}>
                <span>{_date || "--"}</span>
              </ReactTooltip>
            </>
          );
        },
      },
      {
        Header: "Initiated Date",
        accessor: "created",
        id: "created",
        show: is_initiated,
        fixedWidth: 165,
        Cell: ({ cell: { value }, row: { id } }) => {
          const dateFormatted = moment
            .unix(value / 1000)
            .format("MMMM D, YYYY");

          const tooltipId = `initiated-date-tooltip-${value}-${id}`;
          return (
            <Fragment>
              <span
                className="cell changed ellipsis"
                data-tip
                data-for={tooltipId}
              >
                {dateFormatted}
              </span>
              <ReactTooltip id={tooltipId} type="dark" effect="solid">
                <span>{dateFormatted}</span>
              </ReactTooltip>
            </Fragment>
          );
        },
      },
      {
        Header: "Submitted Date",
        accessor: "submitted",
        id: "submitted",
        show: is_presubmitted,
        Cell: ({ cell: { value }, row: { id } }) => {
          const date = moment(value).format("MMMM D, YYYY");
          const tooltipId = `submitted-date-tooltip-${value}-${id}`;
          return (
            <Fragment>
              <span
                className="cell changed ellipsis"
                data-tip
                data-for={tooltipId}
              >
                {date}
              </span>
              <ReactTooltip id={tooltipId} type="dark" effect="solid">
                <span>{date}</span>
              </ReactTooltip>
            </Fragment>
          );
        },
      },
      {
        Header: "Appeals Receipt Date",
        accessor: "appealsReceiptDate",
        id: "appealsReceiptDate",
        fixedWidth: 165,
        show: is_appeal,
        Cell: ({ cell: { value }, row: { id } }) => {
          value = Number(value);
          const _date = value
            ? momentTimezone(value).utc().format("MMMM D, YYYY")
            : null;

          const tooltipId = `appeals-date-tooltip-${value}-${id}`;
          return (
            <Fragment>
              <span
                className="cell changed ellipsis"
                data-tip
                data-for={tooltipId}
              >
                {_date || "--"}
              </span>
              <ReactTooltip id={tooltipId} type="dark" effect="solid">
                <span>{_date || "--"}</span>
              </ReactTooltip>
            </Fragment>
          );
        },
      },
      {
        Header: "Case Status",
        accessor: "externalStatus",
        id: "externalStatus",
        show: !is_initiated_or_presubmitted,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => {
          let status = "default";
          // let id = Math.random();
          if (value) status = value;

          return (
            <>
              <StatusPill status={status.replace(/\s+/g, "-").toLowerCase()}>
                <span data-tip data-for={`externalStatus_${index}`}>
                  {status}
                </span>
              </StatusPill>
              <ReactTooltip
                type="dark"
                effect="solid"
                id={`externalStatus_${index}`}
              >
                <span>{status}</span>
              </ReactTooltip>
            </>
          );
        },
      },
      {
        Header: "Case Status",
        accessor: "external_status",
        id: "external_status",
        show: is_initiated_or_presubmitted,
        Cell: ({
          cell: {
            value,
            row: { index },
          },
        }) => {
          let status = "initiated";
          // let id = Math.random();
          if (value) status = value;
          if (status === "Presubmitted") {
            status = "Submitted";
          }
          return (
            <>
              <StatusPill status={status.replace(/\s+/g, "-").toLowerCase()}>
                <span data-tip data-for={`external_status_${index}`}>
                  {status}
                </span>
              </StatusPill>
              <ReactTooltip
                type="dark"
                effect="solid"
                id={`external_status_${index}`}
              >
                <span>{status}</span>
              </ReactTooltip>
            </>
          );
        },
      },
      {
        Header: "PDF",
        accessor: "caseNumber",
        id: "case_pdf",
        show: !is_initiated_or_presubmitted,
        fixedWidth: 50,
        isButton: true,
        disableSortBy: true,
        Cell: ({ cell: { value } }) => {
          let status = "initiated";
          let id = Math.random();
          if (value) status = value;
          if (status === "Presubmitted") {
            status = "Submitted";
          }
          let [loading, setLoading] = useState(false);
          const handleGetPDF = async (value) => {
            setLoading(true);
            await getpdf(value);
            setLoading(false);
          };
          return (
            <>
              <ReactTooltip id={`${value}-${id}`} type="dark" effect="solid">
                <span>{"View PDF"}</span>
              </ReactTooltip>

              <TDIcon>
                {loading ? (
                  <FaSpinner className="icon-spin" />
                ) : (
                  <FaFilePdf
                    onClick={() => {
                      handleGetPDF(value);
                    }}
                    // Allows keyboard navigation
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleGetPDF(value);
                      }
                    }}
                    tabIndex={0}
                  />
                )}
              </TDIcon>
            </>
          );
        },
      },
    ];
  }
  const columns = useMemo(
    () => memoizedColumns,
    [table_type, caseType, selectedAll, checkedCases]
  );

  // const [allExpanded, setAllExpanded] = useState(false);
  const [tableData, setTableData] = useState({
    rows: EMPTY_CASE_ROWS,
    tableType: null,
    caseType: null,
  });
  const visibleData =
    tableData.tableType === table_type && tableData.caseType === caseType
      ? tableData.rows
      : EMPTY_CASE_ROWS;
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(count);
  const [pageCount, setPageCount] = useState(0);
  const [paginationProps, setPaginationProps] = useState({
    pageIndex: 0,
    pageSize: 0,
  });

  const renderId = useRef(0);
  const requestSequenceRef = useRef(0);
  const activeSubscriptionRef = useRef(null);

  const unsubscribeActiveSubscription = () => {
    if (activeSubscriptionRef.current) {
      activeSubscriptionRef.current.unsubscribe();
      activeSubscriptionRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      // Invalidate pending/debounced requests and prevent post-unmount updates.
      requestSequenceRef.current += 1;
      unsubscribeActiveSubscription();
    };
  }, []);

  const forceRefetch = () => {
    renderId.current++;
  };

  const NoDataConst = (props) => (
    <NoDataComponent noDataResult="There is no data for " {...props} />
  );

  const fetchData = async ({ pageSize, pageIndex, sortBy, requestSequence }) => {
    if (requestSequence !== requestSequenceRef.current) {
      return;
    }

    let sort_ascending = 0;
    let sort_column = "submittedDate";
    let sort_object = sortBy[0]; // multi-sort disabled

    const initiated_presubmitted_columns = [
      "external_status",
      "employer",
      "soc_title",
      "jobTitle",
      "foreign_worker_name",
      "application_id",
    ];
    const initiated_columns = [...initiated_presubmitted_columns, "created"];
    const presubmitted_columns = [
      ...initiated_presubmitted_columns,
      "submitted",
    ];
    const commonColumns = [
      "caseNumber",
      "empBusinessName",
      "jobTitle",
      "foreign_worker_name",
      "dateSubmitted",
      "externalStatus",
    ];
    const submitted_columns = [...commonColumns, "hasActiveNotification"];
    const appeal_columns = [...commonColumns, "appealsReceiptDate"];
    const registration_number_columns = [
      "registrationNumber",
      "empBusinessName",
      "jobTitle",
      "jobMsa",
      "numberOfWorkers",
      "validityStartDate",
      "validityEndDate",
      "status",
    ];

    switch (table_type) {
      case "Initiated":
        if (
          sort_object &&
          sort_object["id"] &&
          !initiated_columns.includes(sort_object["id"])
        ) {
          sort_object = { id: "created", desc: true };
        } else if (!sort_object) {
          sort_object = { id: "created", desc: true };
        }
        break;
      case "Presubmitted":
        if (
          sort_object &&
          sort_object["id"] &&
          !presubmitted_columns.includes(sort_object["id"])
        ) {
          sort_object = { id: "submitted", desc: true };
        } else if (!sort_object) {
          sort_object = { id: "submitted", desc: true };
        }
        break;
      case "Submitted":
      case "Historical":
        if (
          sort_object &&
          sort_object["id"] &&
          !submitted_columns.includes(sort_object["id"])
        ) {
          sort_object = { id: "dateSubmitted", desc: !sort_object["desc"] };
        } else if (!sort_object) {
          sort_object = { id: "dateSubmitted", desc: true };
        }
        break;
      case "Appeal":
        if (
          sort_object &&
          sort_object["id"] &&
          !appeal_columns.includes(sort_object["id"])
        ) {
          sort_object = { id: "dateSubmitted", desc: !sort_object["desc"] };
        } else if (!sort_object) {
          sort_object = { id: "dateSubmitted", desc: true };
        }
        break;
      case "RegistrationNumber":
        if (
          // refactor all of these sort_object checks into a function
          (sort_object &&
            sort_object["id"] &&
            !registration_number_columns.includes(sort_object["id"])) ||
          !sort_object
        ) {
          sort_object = {
            id: "registrationNumber",
            desc: true,
          };
        }
        break;
      default:
    }

    if (sort_object) {
      sort_column = sortColumnResolver(sort_object["id"]);
      sort_ascending = !sort_object["desc"] ? 1 : -1;
    }

    let variables = {
      UISorting: {
        type: table_type,
        caseType: caseType,
        limit: pageSize,
        page: pageIndex,
        sort_ascending,
        sort_column,
      },
      UISearch: filterBy,
    };

    unsubscribeActiveSubscription();

    const subscription = apolloClient
      .watchQuery({
        query,
        variables,
        pollInterval: 0,
        fetchPolicy: "network-only",
      })
      .subscribe(({ data, error, loading }) => {
        if (requestSequence !== requestSequenceRef.current) {
          return;
        }

        if (error) {
          console.error(error);
        }

        if (!loading && data) {
          const gql_data = data[`getPaginatedData`][`${table_type}Cases`];
          const new_page_count = data[`getPaginatedData`]["total_count"];

          setTableData({ rows: gql_data, tableType: table_type, caseType });
          setTotalCount(new_page_count);
          setPageCount(Math.ceil(new_page_count / pageSize));
          setLoading(false);
          return;
        }

        if (error && !data) {
          setLoading(false);
        }
      });

    activeSubscriptionRef.current = subscription;
  };

  const debouncedFetchData = useDebouncedFunction(fetchData, 250);

  const handleFetchData = useCallback(
    (params) => {
      requestSequenceRef.current += 1;
      const requestSequence = requestSequenceRef.current;

      // Immediately invalidate/cleanup active request before debounce delay.
      unsubscribeActiveSubscription();
      setLoading(true);
      debouncedFetchData({
        ...params,
        requestSequence,
      });
    },
    [debouncedFetchData, setLoading]
  );

  let showCaseTableActions =
    ["Initiated", "Submitted", "Historical", "Appeal"].includes(table_type) &&
    checkedCases &&
    checkedCases.length > 0;

  const { pageIndex, pageSize } = paginationProps;
  let paginationRangeStart = pageIndex * pageSize + 1;
  let paginationRangeEnd = Math.min((pageIndex + 1) * pageSize, totalCount);

  const paginationTableNames = {
    Presubmitted: "Pending Case Number",
    Submitted: "Assigned Case Number",
    RegistrationNumber: "Registration Numbers",
  };

  // setup modal window
  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = function () {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={toggleModal}
        contentLabel="Refine Search"
        className="refineSearchModal"
        overlayClassName="refineSearchOverlay"
      >
        <button onClick={toggleModal} className="caseFilterClose modal-exit-x">
          <MdClose className="close-icon"></MdClose>
        </button>
        <div>
          <CasesFilter toggleModal={toggleModal} />
        </div>
      </Modal>
      <CaseCounter>
        <div style={{ fontSize: "0.95rem" }}>
          {`${isNaN(paginationRangeStart) ? 1 : paginationRangeStart} - ${
            isNaN(paginationRangeEnd) ? 1 : paginationRangeEnd
          } of ${totalCount || 0} ${
            paginationTableNames[table_type] || table_type
          }`}
        </div>
        {showCaseTableActions && (
          <CaseTableActions
            setSelectedAll={setSelectedAll}
            forceRefetch={forceRefetch}
            checkedCases={checkedCases}
            setCheckedCases={setCheckedCases}
            numberChecked={checkedCases.length}
            isInitiated={table_type === "Initiated"}
            isPresubmitted={table_type === "Presubmitted"}
            groupRole={groupRole}
            groupMembers={groupMembers}
            tab={table_type}
            formId={caseType}
            filterBy={filterBy}
            refetchTabsTable={refetchTabsTable}
          />
        )}
        <RefineSearch>
          <div className="refineSearch">
            <button className="refineButton" onClick={toggleModal}>
              Refine results <FaFilter />
            </button>
          </div>
        </RefineSearch>
      </CaseCounter>
      <ReactTable
        columns={columns}
        data={visibleData}
        pageSizeOptions={[10, 25, 50, 100]}
        pageCount={pageCount}
        loading={loading}
        NoDataComponent={NoDataConst}
        tableTheme="casesTableTheme"
        tableStyle={{ minWidth: "1145px" }}
        allowUnsortedState={true}
        getPaginationProps={setPaginationProps}
        onDataFetch={() => {
          setSelectedAll(false);
          setCheckedCases([]);
        }}
        renderRowSubComponent={renderRowSubComponent}
        fetchData={handleFetchData}
        refetchDependencies={[renderId.current]}
        refetchToFirstPageDependencies={[table_type, caseType, filterBy]}
      />
    </>
  );
}

export default (props) => (
  <ApolloConsumer>
    {(client) => <CasesTable apolloClient={client} {...props} />}
  </ApolloConsumer>
);
